/**
 * TaskSidebarSection Component
 * Collapsible section of tasks with title, count badge, total duration, and chevron toggle.
 */

import { useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import type { Task } from '@signalvote/shared';
import { cn } from '@/lib/utils';
import { TaskCard } from './TaskCard';

// ============================================================================
// Types
// ============================================================================

export interface TaskSidebarSectionProps {
  /** Section title (e.g., "Scheduled on calendar", "Today", "Done") */
  title: string;
  /** Tasks to display in this section */
  tasks: Task[];
  /** Current user's ID for assignment badge logic */
  currentUserId?: string;
  /** If provided, shows count chip next to title */
  badgeCount?: number;
  /** Whether to use error color for badge (for Overdue section) */
  badgeError?: boolean;
  /** Called when a task checkbox is toggled */
  onTaskCheckboxChange: (taskId: string) => void;
  /** Called when a task card is clicked */
  onTaskClick: (task: Task) => void;
  /** Called when Accept button is clicked */
  onAccept?: (taskId: string) => void;
  /** Called when Reject button is clicked */
  onReject?: (taskId: string) => void;
  /** Called when an assignee is selected via the inline assign button */
  onAssign?: (taskId: string, assigneeId: string, assigneeName: string) => void;
  /** Whether task cards in this section can be dragged */
  draggable?: boolean;
  /** Whether the section starts collapsed */
  defaultCollapsed?: boolean;
  /** Total duration label to show on the right (e.g., "1h", "15m") */
  totalDuration?: string | null;
  /** Additional class names for the section title text */
  titleClassName?: string;
  /** Hide duration chips on individual task cards */
  hideDuration?: boolean;
  /** Show date/time chip on task cards (for "show all tasks" mode) */
  showDateChip?: boolean;
  /** Custom empty state message (overrides default per-section messages) */
  emptyMessage?: string;
}

// ============================================================================
// Component
// ============================================================================

export function TaskSidebarSection({
  title,
  tasks,
  currentUserId,
  badgeCount,
  badgeError = false,
  onTaskCheckboxChange,
  onTaskClick,
  onAccept,
  onReject,
  onAssign,
  draggable,
  defaultCollapsed = false,
  totalDuration,
  titleClassName,
  hideDuration = false,
  showDateChip = false,
  emptyMessage,
}: TaskSidebarSectionProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const count = badgeCount ?? tasks.length;

  return (
    <div className="mb-3">
      {/* Section Header */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="flex items-center w-full gap-2 mb-1.5 px-2 py-1 hover:bg-accent/50 rounded-sm transition-colors cursor-pointer select-none"
      >
        <span
          className={cn(
            'font-semibold text-[0.7rem] tracking-wide',
            titleClassName ?? 'text-muted-foreground'
          )}
        >
          {title}
        </span>
        {count > 0 && (
          <span
            className={cn(
              'inline-flex items-center justify-center h-[18px] px-1.5 text-[0.65rem] font-semibold rounded-full',
              badgeError
                ? 'bg-destructive text-destructive-foreground'
                : 'bg-muted text-muted-foreground'
            )}
          >
            {count}
          </span>
        )}
        <span className="flex-1" />
        {totalDuration && (
          <span className="text-[0.65rem] text-muted-foreground font-medium">
            {totalDuration}
          </span>
        )}
        {collapsed ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        ) : (
          <ChevronUp className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        )}
      </button>

      {/* Task List */}
      {!collapsed && (
        <>
          {tasks.length === 0 ? (
            <p className="text-muted-foreground/50 px-2 py-2 text-[0.75rem]">
              {emptyMessage ?? 'No tasks'}
            </p>
          ) : (
            <div className="flex flex-col gap-2 px-1">
              {tasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  currentUserId={currentUserId}
                  draggable={draggable}
                  onCheckboxChange={onTaskCheckboxChange}
                  onClick={onTaskClick}
                  onAccept={onAccept}
                  onReject={onReject}
                  onAssign={onAssign}
                  hideDuration={hideDuration}
                  showDateChip={showDateChip}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default TaskSidebarSection;
